import blankAnamnesisImg from '@/assets/images/blank-anamnesis.webp';
import { Button } from '@/components/ui/button';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { cn } from '@/lib/utils';

type Props = {
    uploading: boolean;
    disabled: boolean;
    onFilesSelected: (files: File[]) => void;
    onWrite: () => void;
};

/**
 * The two ways an anamnesis gets into a report: a file the psychologist already
 * has, or the text typed straight into the editor. They are equivalent routes,
 * so they are two equal cards — the writing side used to be a 12px link under a
 * full-width dropzone.
 *
 * The upload card *is* the dropzone: dragging a file onto it works, and clicking
 * it opens the file picker. The writing card mirrors `FileDropzone`'s own card
 * recipe — same shell, same art width, same type scale — so the two read as a
 * pair rather than a drop target next to a card.
 */
export default function AnamnesisSourceChoice({
    uploading,
    disabled,
    onFilesSelected,
    onWrite,
}: Props) {
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            <FileDropzone
                title="Enviar arquivo"
                description=".docx, .txt ou .pdf"
                accept=".docx,.txt,.pdf"
                loading={uploading}
                disabled={disabled}
                showSelectButton={false}
                className="h-full"
                onFilesSelected={onFilesSelected}
            />

            {/* Same construction as TemplateOptionCard: the visible surface is
                the card and the control is a stretched, neutralized overlay, so
                nothing is nested inside a wrapper pretending to be a button. */}
            <div
                className={cn(
                    'group relative flex h-full flex-col items-center rounded-3xl border border-dashed border-border-strong bg-surface p-6 text-center transition-colors',
                    uploading ? 'opacity-70' : 'hover:bg-muted/70',
                )}
            >
                <Button
                    variant="ghost"
                    onClick={onWrite}
                    disabled={uploading}
                    aria-label="Escrever no LaudoPsi"
                    className="absolute inset-0 size-auto rounded-3xl p-0 hover:bg-transparent"
                />

                <div className="pointer-events-none relative z-10 flex flex-col items-center">
                    <img
                        src={blankAnamnesisImg}
                        alt=""
                        // Same intrinsic size as the upload art, so the two
                        // cards line up without a fixed height.
                        width={476}
                        height={387}
                        decoding="async"
                        className="mb-3 h-auto w-[140px] max-w-full"
                    />
                    <span className="block text-sm font-semibold text-foreground">
                        Escrever no LaudoPsi
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                        Cole ou escreva o texto
                    </span>
                </div>
            </div>
        </div>
    );
}
